import { isAxiosError, type AxiosError } from 'axios';
import type { HttpError } from '@refinedev/core';

/** One entry of FastAPI's pydantic-validation ``detail`` array. */
export interface ApiErrorDetailItem {
  type?: string;
  msg?: string;
  loc?: (string | number)[];
  ctx?: Record<string, unknown>;
}

/** What ``asHttpError`` hands to Refine: an ``HttpError`` plus the raw entries. */
export type ApiHttpError = HttpError & { detailItems?: ApiErrorDetailItem[] };

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** ``loc`` without the leading request-part frame: ``size``, ``links.0.position``. */
export const formatLoc = (loc: unknown): string | undefined => {
  if (!Array.isArray(loc) || loc.length === 0) return undefined;
  const parts = loc.filter(
    (p) => typeof p === 'string' || typeof p === 'number'
  );
  const scoped =
    parts.length > 1 && (parts[0] === 'body' || parts[0] === 'query')
      ? parts.slice(1)
      : parts;
  return scoped.length > 0 ? scoped.join('.') : undefined;
};

/**
 * ``field: msg`` lines out of a FastAPI error body — one per pydantic entry,
 * or a single line for a domain-error string ``detail``. Empty when the body
 * carries nothing usable, which is the caller's cue to fall back.
 */
const detailLines = (data: unknown): string[] => {
  if (!isRecord(data)) return [];
  const { detail } = data;

  if (isNonEmptyString(detail)) return [detail];
  if (!Array.isArray(detail)) return [];

  return detail
    .filter(isRecord)
    .map((item) => {
      if (!isNonEmptyString(item.msg)) return undefined;
      const field = formatLoc(item.loc);
      return field ? `${field}: ${item.msg}` : item.msg;
    })
    .filter(isNonEmptyString);
};

/** Human-readable text from a FastAPI error body, for a toast (multi-line). */
export const extractDetailMessage = (data: unknown): string | undefined => {
  const lines = detailLines(data);
  return lines.length > 0 ? lines.join('\n') : undefined;
};

/**
 * Human-readable message from a backend error response, or undefined
 * when the response carries nothing usable and a local fallback should
 * be shown instead. Single-line: for notification bodies that do not
 * render line breaks.
 */
export const extractBackendMessage = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) return undefined;
  const data = error.response?.data as
    | { detail?: unknown; message?: unknown }
    | undefined;
  const lines = detailLines(data);
  if (lines.length > 0) return lines.join('; ');
  return isNonEmptyString(data?.message) ? data.message : undefined;
};

/** The raw pydantic entries, for consumers that need ``type`` or ``ctx``. */
export const extractDetailItems = (data: unknown): ApiErrorDetailItem[] => {
  if (!isRecord(data) || !Array.isArray(data.detail)) return [];
  return data.detail.filter(isRecord).map((item) => ({
    type: isNonEmptyString(item.type) ? item.type : undefined,
    msg: isNonEmptyString(item.msg) ? item.msg : undefined,
    loc: Array.isArray(item.loc)
      ? item.loc.filter(
          (p): p is string | number =>
            typeof p === 'string' || typeof p === 'number'
        )
      : undefined,
    ctx: isRecord(item.ctx) ? item.ctx : undefined,
  }));
};

/**
 * Refine's server-side validation contract: ``{field: [msg]}`` keyed by the
 * dotted ``loc`` path. Entries the backend did not attribute to a field are
 * left out — the toast is their only surface.
 */
export const extractFieldErrors = (
  data: unknown
): HttpError['errors'] | undefined => {
  const errors: Record<string, string[]> = {};

  for (const item of extractDetailItems(data)) {
    const field = formatLoc(item.loc);
    if (!field || !item.msg) continue;
    (errors[field] ??= []).push(item.msg);
  }

  return Object.keys(errors).length > 0 ? errors : undefined;
};

/**
 * Make a rejected axios error satisfy Refine's ``HttpError`` so default
 * toasts read the backend text and ``useForm`` can paint named fields.
 * Augments in place rather than replacing: call sites across the app still
 * narrow it with ``isAxiosError`` and read ``response.data``.
 */
export const asHttpError = (
  error: AxiosError
): AxiosError & HttpError & { detailItems: ApiErrorDetailItem[] } => {
  const data = error.response?.data;
  const augmented = error as AxiosError &
    HttpError & { detailItems: ApiErrorDetailItem[] };
  augmented.message = extractDetailMessage(data) ?? error.message;
  augmented.statusCode = error.response?.status ?? 0;
  augmented.errors = extractFieldErrors(data);
  augmented.detailItems = extractDetailItems(data);
  return augmented;
};
